import { UserRole, UserStatus } from "~/gql/graphqlBase.generated";
import i18n from "~/i18n/i18n";

export const USER_ROLE_LABELS: Partial<Record<UserRole, string>> = {
	[UserRole.Admin]: "Admin",
	[UserRole.Finance]: "Finance",
	[UserRole.Portal]: "Portal",
	[UserRole.Sales]: "Sales",
	[UserRole.SalesManager]: "Sales Manager",
	[UserRole.NoOrderRestrictions]: "No Order Restrictions",
	[UserRole.EdiManager]: "EDI Manager",
	[UserRole.EdiViewer]: "EDI Viewer",
	[UserRole.RestockOrderer]: "Restock Orderer",
	[UserRole.VerifyDispatcher]: "Verify Dispatcher",
	[UserRole.VerifyScanner]: "Verify Scanner",
	[UserRole.VerifyManager]: "Verify Manager",
	[UserRole.Production]: "Production",
	[UserRole.ProduceAdmin]: "Produce Admin"
};

export const USER_ROLE_DESCRIPTIONS: Partial<Record<UserRole, string>> = {
	[UserRole.Admin]:
		"Full access to settings and user management. An Admin sees every customer and site, and Customer, Site and Route assignments do not limit them. Give this to the people who run the system day to day.",
	[UserRole.Finance]:
		"Opens the Billing page, where subscription and payment details are managed. It does not limit anything else this person can do.",
	[UserRole.Sales]: "Creates and manages their own orders and customers. Give this to salespeople and account reps.",
	[UserRole.Portal]:
		"A customer signing in to place and track their own orders. Granted only through portal-user provisioning, never by assigning a customer to a staff user.",
	[UserRole.NoOrderRestrictions]:
		"Legacy role, no longer assignable. For a non-Admin user, seeing all customers and sites vs. a subset is controlled by the Customer/Site assignment on the user, not by this role.",
	[UserRole.EdiManager]:
		"Sets up and manages automated EDI order exchange with trading partners. Give this to the person who owns your EDI setup. Requires the EDI feature to be enabled for your business.",
	[UserRole.EdiViewer]:
		"Views EDI orders and their status without changing the setup. Give this to staff who monitor EDI activity. Requires the EDI feature to be enabled for your business.",
	[UserRole.RestockOrderer]:
		"Raises restock and replenishment orders to top up inventory. Give this to whoever keeps stock levels up. Requires the Restock feature to be enabled for your business.",
	[UserRole.VerifyDispatcher]:
		"Sends orders into the Verify dispatch queue and oversees what ships. Give this to dispatch coordinators. Requires the Verify feature to be enabled for your business.",
	[UserRole.VerifyScanner]:
		"Scans and checks items against orders on the floor during Verify. Give this to the staff doing the scanning. Requires the Verify feature to be enabled for your business.",
	[UserRole.VerifyManager]:
		"Oversees the whole Verify process and its results across dispatch and scanning. Give this to Verify supervisors. Requires the Verify feature to be enabled for your business.",
	[UserRole.Production]:
		"Plans production. When PRODUCE Settings limits plan creation from orders to production staff, people with this role (and Admins) can create a production plan from an order."
};

export const USER_ROLE_OPTIONS: UserRole[] = [
	UserRole.Admin,
	UserRole.Finance,
	UserRole.Sales,
	UserRole.SalesManager,
	UserRole.Production,
	UserRole.ProduceAdmin,
	UserRole.EdiManager,
	UserRole.EdiViewer,
	UserRole.RestockOrderer,
	UserRole.VerifyDispatcher,
	UserRole.VerifyScanner,
	UserRole.VerifyManager
];

export const ACCESS_SCOPE_EXPLANATION =
	"Pick the roles for this person. Open the list to see what each role grants. An Admin always sees every customer and site. A route can still be stored on an Admin, but it does not limit them. For every other role, which customers, sites and routes this user can see and change depends on their Customer, Site and Route assignments.";

export const ADMIN_ASSIGNMENT_NOTE =
	"Admin sees every customer and site, so Customer and Site assignments are not offered for this role. A route can still be stored on an Admin, but it does not limit them.";

export interface RoleFeatureFlags {
	edi: boolean;
	restock: boolean;
	verify: boolean;
}

const EDI_ROLES: UserRole[] = [UserRole.EdiManager, UserRole.EdiViewer];
const VERIFY_ROLES: UserRole[] = [UserRole.VerifyDispatcher, UserRole.VerifyScanner, UserRole.VerifyManager];
const RESTOCK_ROLES: UserRole[] = [UserRole.RestockOrderer];

const isRoleEnabled = (role: UserRole, flags: RoleFeatureFlags): boolean => {
	if (EDI_ROLES.includes(role)) return flags.edi;
	if (VERIFY_ROLES.includes(role)) return flags.verify;
	if (RESTOCK_ROLES.includes(role)) return flags.restock;
	return true;
};

const RETIRED_USER_ROLES: string[] = [UserRole.Maintenance, UserRole.Ops];

export const withoutRetiredRoles = <R extends string>(roles: readonly R[]): R[] =>
	roles.filter((role) => !RETIRED_USER_ROLES.includes(role));

export const getAssignableUserRoles = (flags: RoleFeatureFlags): UserRole[] =>
	USER_ROLE_OPTIONS.filter((role) => isRoleEnabled(role, flags));

export const formatUserRoleDescription = (role: UserRole): string =>
	role === UserRole.SalesManager
		? i18n.t("admin.users.roles.salesManagerDescription", { ns: "common" })
		: role === UserRole.ProduceAdmin
			? i18n.t("admin.users.roles.produceAdminDescription", { ns: "common" })
			: (USER_ROLE_DESCRIPTIONS[role] ?? "");

export const formatUserRole = (role: UserRole): string => USER_ROLE_LABELS[role] ?? role;

export const formatUserRoles = (roles: UserRole[]): string => withoutRetiredRoles(roles).map(formatUserRole).join(", ");

export const isLegacyUserRole = (role: UserRole): boolean => role === UserRole.NoOrderRestrictions;

export const formatUserStatus = (status: UserStatus): string => (status === UserStatus.Active ? "Active" : "Inactive");

export const formatMultiFactorStatus = (enabled: boolean): string => (enabled ? "Enabled" : "Not enabled");

export const losesSalesRole = (previousRoles: UserRole[], nextRoles: UserRole[]): boolean =>
	[UserRole.Sales, UserRole.SalesManager].some((role) => previousRoles.includes(role) && !nextRoles.includes(role));

interface CustomerScopedUser {
	assignedCustomerIds?: string[];
	customerId?: string | null;
	roles: UserRole[];
}

export const isPortalUser = (user: CustomerScopedUser): boolean =>
	Boolean(user.customerId) || user.roles.includes(UserRole.Portal);
